<template>
  <div class="decks-wrapper">
    <div class="content container">
      <div class="row">
        <div class="types-tabs col col-12">
          <div class="tabs">
            <li v-for="tab in tabs" :key="tab.name" :class="{ active: currentTab === tab.name }">
              <a @click="changeTab(tab.name)">
                {{ tab.text }}
              </a>
            </li>
          </div>
        </div>

        <slot v-if="showForm" name="form" />

        <div v-if="!showDecks && !showForm && !deckResults" class="no-results">
          <h5>
            {{ currentTab === 'in-progress' ? 'You don’t have any active decks yet. Start working with the flashcards and track your study progress here.' : 'You haven’t completed any decks yet. Your flashcards will appear here once you complete a whole deck.' }}
          </h5>
        </div>

        <div v-if="showDecks" :key="currentTab" class="main col col-8 md-8 offset-1 md-offset-0">
          <div class="search">
            <div class="search-holder">
              <SearchBar placeholder="Search decks" @search="search" />
              <client-only>
                <Filters
                  v-if="$store.state.layout === 'mobile'"
                  :filters="filters" applied-counter :reset="filtersReset"
                  :de-selected="deSelected"
                  class="mobile-filter" @change="filtrate"
                />
              </client-only>
            </div>
            <SearchResults
              key="searchLabels" :filters="filters" :labels="searchLabels"
              @closeLabel="close"
              @clearAll="resetFilters = true"
            />
          </div>
          <div class="row results">
            <Card
              v-for="(deck, id) in decks.data" :key="id" :deck="deck"
              heading="h2" class="col-6 sm-6 md-6"
            />
          </div>
          <Pagination
            v-if="decks.count > 10" :page="page" :total="decks.count"
            :per-page="10" align="center"
            :links="false" @pagechanged="setPage"
          />
        </div>
        <aside v-if="showDecks" class="col col-3 md-4">
          <FiltersAccordion
            :filters="filters" :reset="filtersReset" :de-selected="deSelected"
            class="desktop-filter"
            @change="filtrate"
          />
        </aside>
      </div>
    </div>
    <section class="faq">
      <FrequentlyAskedQuestions group="decks" :results="data.faq" />
    </section>
  </div>
</template>

<script>
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions';
import FiltersAccordion from '../Courses/fa/FaFiltersAccordion';
import SearchResults from '../Business/SearchResults';
import Filters from '../components/Filters';
import Pagination from '../components/Pagination';
import SearchBar from '../Business/SearchBar';
import Card from './Card';

export default {
  components: {
    FrequentlyAskedQuestions,
    FiltersAccordion,
    SearchResults,
    Filters,
    Pagination,
    Card,
    SearchBar,
  },
  props: {
    data: { type: Object, required: true, default: () => ({}) },
  },
  data() {
    return {
      page: 1,
      decks: this.data.decks,
      keyword: '',
      filter: '',
      searchLabels: {
        results: this.data.decks.count,
        searchData: '',
        filtersData: {},
      },
      deSelectedOption: {},
      resetFilters: false,
      currentTab: 'all',
      tabs: [
        { name: 'all', text: 'All decks' },
        {
          name: 'in-progress', text: 'In progress', size: 0, originalSize: null,
        },
        {
          name: 'completed', text: 'Completed', size: 0, originalSize: null,
        },
      ],
      deckResults: false,
    };
  },
  computed: {
    filters() {
      const topics = this.data.topics.reduce((p, t) => ({ ...p, [t.id]: { text: t.name } }), {});
      const authors = this.data.authors.reduce((p, t) => ({ ...p, [t.toLowerCase()]: { text: t } }), {});

      return {
        topic: {
          name: 'Topics',
          type: 'multiple',
          values: topics,
          open: true,
        },
        cardCount: {
          name: 'Deck Size',
          values: {
            1: { text: '<19 Cards' },
            2: { text: '20-49 Cards' },
            3: { text: '>50 Cards' },
          },
          open: true,
        },
        author: {
          name: 'Authors',
          type: 'multiple',
          values: authors,
          open: true,
        },
      };
    },
    deSelected() {
      return this.deSelectedOption;
    },
    clearSearch() {
      return this.keyword;
    },
    filtersReset() {
      return this.resetFilters;
    },
    showForm() {
      return this.currentTab !== 'all' && !this.$store.state.auth.user;
    },
    showDecks() {
      return !!((this.currentTab === 'all') || (this.currentTab !== 'all' && this.decks.count && this.$store.state.auth.user));
    },
  },
  methods: {
    async fetchDecks() {
      this.deckResults = true;
      const data = await this.$axios.$get(`/flashcards?page=${this.page}&tab=${this.currentTab}&q=${this.keyword}&${this.filter}&initial_data=1`, { progress: false });
      this.deckResults = false;

      this.decks = data.decks;
      this.searchLabels.results = data.decks.count;
      this.searchLabels.searchData = this.keyword;
    },
    close(label) {
      if (label.type === 'search') {
        this.keyword = '';
      } else {
        this.deSelectedOption = label;
      }
    },
    async changeTab(tab) {
      this.$emit('tabChange', 'login');
      this.keyword = '';
      this.currentTab = tab;
      this.page = 1;
      this.decks = [];
      this.fetchDecks();
    },
    async filtrate(f) {
      setTimeout(() => {
        this.resetFilters = false;
      }, 1000);

      this.page = 1;
      this.filter = f.params;
      this.searchLabels.filtersData = f.filter;
      await this.fetchDecks();
    },
    setPage(page) {
      this.page = page;
      this.fetchDecks();
      let offset;
      if (this.$store.state.layout === 'mobile' && !this.$store.state.tablet) {
        offset = 330;
      } else {
        offset = 270;
      }
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    },
    search(k) {
      this.keyword = k;
      this.fetchDecks();
    },
  },
};
</script>

<style lang="scss" scoped>
.decks-wrapper {
  margin-top: var(--spacing-80);
  padding-top: var(--spacing-60);
  background: var(--main-light-shade-1);

  section {
    &.faq {
      padding-bottom: 114px;
      margin-top: var(--spacing-80);

      a {
        color: var(--accent);
      }

      ::v-deep .decks .col-10 {
        padding-left: 0;

        .body-1 a {
          color: var(--accent);
        }
      }
    }
  }

  .content {
    .mobile-filter {
      display: none;
    }

    aside {
      order: 1;
    }

    .main {
      order: 2;
    }

    .types-tabs {
      order: 0;
      margin-bottom: 48px;

      .tabs {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        align-items: center;

        li {
          margin: 0;
          position: relative;
          min-width: 170px;
          text-align: center;

          a {
            color: var(--main-light-shade-4) !important;
            border-bottom: 3px solid var(--main-light-shade-4);
            padding-bottom: var(--spacing-8);
            text-decoration: none;
            font-weight: bold;
            cursor: pointer;
            display: block;
          }

          &.active a {
            color: var(--accent) !important;
            border-bottom-color: var(--accent);
          }
        }
      }
    }

    .no-results {
      width: 100%;
      text-align: center;

      h5 {
        color: var(--main-dark-light-4);
        font-weight: 500;
        line-height: 110%;
        margin: var(--spacing-20);
      }
    }

    ::v-deep {
      .filters-accordion {
        .head,
        .filter-name {
          color: var(--main-dark-light-3);
          font-size: 16px;
          line-height: 167%;
          font-weight: 400;
        }

        .head {
          margin-bottom: var(--spacing-30);
        }

        .filters-dropdown ul li {
          background: var(--main-light-shade-3);
          color: var(--main-dark-light-3);

          &:hover {
            background: var(--main-dark-shade-4);
          }

          &.selected {
            color: var(--accent-shade-3);
            background: var(--accent-light-3);
          }
        }
      }

      .search {
        .results-wrapper .search-results {
          font-size: 16px;
          line-height: 167%;
        }

        .search-bar {
          input {
            border-color: var(--main-light-shade-2);
            padding: var(--spacing-6) 54px;
            font-size: 16px;
            line-height: 26px;
          }

          .search-icon {
            top: 10px;
          }

          .close-btn {
            top: 13px;
          }
        }
      }

      .pagination {
        margin: 0;
      }
    }
  }

  ::v-deep .faq {
    .h3 {
      font-size: 28px;
      line-height: 120%;
    }

    .body-1 {
      font-size: 16px;
      margin-top: 0;
    }
  }

  @include bp($bp-laptop) {
    margin-top: 78px;
    padding-top: var(--spacing-60);

    h2,
    .h2 {
      font-size: 38px;
      line-height: 45px;
    }

    h3,
    .h3 {
      font-size: 32px;
      line-height: 40px;
    }

    h4,
    .h4 {
      font-size: 24px;
      line-height: 30px;
    }

    section {
      &.info {
        ol li .content .title {
          font-size: 18px;
          line-height: 139%;
        }
      }
    }

    ::v-deep .faq {
      .h3 {
        font-size: 24px;
      }
    }
  }

  @include bp($bp-phone) {
    &.site {
      .hero-section {
        .h3 {
          font-size: 28px;
          line-height: 110%;
        }

        .description {
          line-height: 150%;
        }
      }
    }

    .content {
      .types-tabs {
        margin-bottom: var(--spacing-20);

        .tabs {
          flex-direction: column;
          align-items: flex-start;

          li {
            width: 100%;

            a {
              text-align: left;
              border-bottom: 0;
              border-left: 3px solid var(--main-light-shade-4);
              padding: var(--spacing-12);
            }

            &.active a {
              color: var(--accent) !important;
              border-left-color: var(--accent);
            }
          }
        }
      }
    }
  }

  @include bp($bp-mobile) {
    margin-top: 68px;
    padding-top: var(--spacing-40);

    .hero-section {
      padding: var(--spacing-40) 0;
      margin-bottom: var(--spacing-40) !important;

      .description {
        padding-right: 0;
      }

      h2 {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    section {
      margin-top: 80px;

      &.info {
        margin-top: var(--spacing-40);
        margin-bottom: var(--spacing-50);

        .title {
          font-size: 24px;
        }

        ol li .content {
          .title {
            font-size: 18px;
            line-height: 139%;
          }

          .description {
            margin-bottom: var(--spacing-30);
          }
        }
      }

      &.faq {
        ::v-deep .h3 {
          font-size: 21px;
        }
      }

      >.container > h3 {
        font-size: 28px;
      }
    }

    .content {
      .no-results h5 {
        font-size: 18px;
      }

      .desktop-filter {
        display: none;
      }

      .mobile-filter {
        display: block;
      }

      .row {
        margin-left: -12px;
        margin-right: -12px;
      }

      .types-tabs {
        padding-left: 15px;
      }

      .search .search-holder {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .search-bar-wrapper {
          margin-right: 17px;
        }
      }

      ::v-deep {
        .pagination {
          margin-top: var(--spacing-20);
        }

        .__filters {
          &.open {
            .__filters-dropdown {
              padding-top: var(--spacing-16);
              padding-bottom: var(--spacing-16);
              top: 68px;
            }
          }

          .__filters-dropdown {
            .__filters-dropdown-body > div {
              color: var(--main-dark);
              font-weight: 500;

              ul li {
                background: var(--main-light-shade-3);
                color: var(--main-dark-light-3);

                &.selected {
                  color: var(--accent-shade-3);
                  background: var(--accent-light-3);
                }
              }
            }

            .__filters-dropdown-footer .clear-btn {
              color: var(--main-light-shade-4);

              svg path[fill] {
                fill: var(--main-light-shade-4);
              }
            }
          }
        }
      }
    }
  }
}

.fa {
  .decks-wrapper {
    background: var(--main-light-shade-2);

    .hero-section {
      background: linear-gradient(179.58deg, rgba(3, 111, 253, 0.14) -0.05%, rgba(3, 111, 253, 0.05) 25.66%, rgba(1, 22, 51, 0) 99.77%), var(--main-dark);
    }

    .content {
      ::v-deep {
        .filters-accordion .filters-dropdown ul li {
          background: var(--main-light-shade-2-200);
          color: var(--main-dark-light-3);

          &:hover {
            background: var(--main-light-shade-3);
          }

          &.selected {
            color: var(--accent);
            background: var(--accent-light-2);
          }
        }

        .search .search-bar input {
          border-color: var(--main-light-shade-3);
        }

        .results-wrapper {
          .search-label {
            background: var(--main-light-shade-2-200);
          }
        }
      }
    }
  }

  section.introduction > .container > h3 {
    color: var(--accent-shade-1);
  }

  .decks-wrapper section.structure .tabs .tab {
    background: var(--main-light-shade-2-200);

    &.active {
      background: rgba(156, 163, 184, 0.5);
    }
  }

  @include bp($bp-mobile) {
    .decks-wrapper .content {
      ::v-deep {
        .__filters .__filters-dropdown .__filters-dropdown-body > div {
          ul li {
            background: var(--main-light-shade-2-200);
            color: var(--main-dark-light-3);

            &.selected {
              color: var(--accent);
              background: var(--accent-light-2);
            }
          }
        }
      }
    }
  }
}
</style>
